import React, { useState, useEffect, useRef } from 'react';
import './EnrollmentModal.css';
import AccessCodeDisplay from './components/AccessCodeDisplay';
import { getAppConfig, DEFAULT_APP } from './constants/mobileApps';

const EnrollmentModal = ({ isOpen, onClose, studyData }) => {
    // Enrollment flow state
    const [currentStep, setCurrentStep] = useState('eligibility');
    const [eligibilityAnswers, setEligibilityAnswers] = useState({});
    const [consents, setConsents] = useState({ mandatory: {}, optional: {} });
    const [hasSignature, setHasSignature] = useState(false);
    const [signatureDataUrl, setSignatureDataUrl] = useState(null);
    const [participantName, setParticipantName] = useState('');
    const [participantEmail, setParticipantEmail] = useState('');
    const [participantEmailConfirm, setParticipantEmailConfirm] = useState('');
    const [accessCode, setAccessCode] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    // Signature canvas ref
    const signatureCanvasRef = useRef(null);
    const signatureCtxRef = useRef(null);
    const isDrawingRef = useRef(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setCurrentStep('eligibility');
            setEligibilityAnswers({});
            setHasSignature(false);
            setSignatureDataUrl(null);
            setParticipantName('');
            setParticipantEmail('');
            setParticipantEmailConfirm('');
            setAccessCode('');
            setIsProcessing(false);

            // Initialize consent state
            if (studyData?.eConsent) {
                const mandatoryInit = {};
                const optionalInit = {};
                studyData.eConsent.mandatoryItems?.forEach(item => {
                    mandatoryInit[item.id] = false;
                });
                studyData.eConsent.optionalItems?.forEach(item => {
                    optionalInit[item.id] = false;
                });
                setConsents({ mandatory: mandatoryInit, optional: optionalInit });
            }

            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [isOpen, studyData]);

    // Initialize signature canvas when consent step is active
    useEffect(() => {
        if (currentStep === 'consent' && signatureCanvasRef.current) {
            const canvas = signatureCanvasRef.current;
            const ctx = canvas.getContext('2d');

            const rect = canvas.getBoundingClientRect();
            canvas.width = rect.width;
            canvas.height = rect.height;

            ctx.strokeStyle = '#003E74';
            ctx.lineWidth = 2;
            ctx.lineCap = 'round';

            signatureCtxRef.current = ctx;
        }
    }, [currentStep]);

    // Eligibility screening
    const selectAnswer = (questionId, answer) => {
        setEligibilityAnswers(prev => ({ ...prev, [questionId]: answer }));
    };

    const checkEligibility = () => {
        const questions = studyData?.screeningSurvey?.questions || [];
        let allAnswered = true;
        let eligible = true;
        let failedQuestion = null;

        for (const q of questions) {
            if (eligibilityAnswers[q.id] === undefined) {
                allAnswered = false;
            } else if (eligibilityAnswers[q.id] !== q.eligibleAnswer) {
                eligible = false;
                if (!failedQuestion) failedQuestion = q;
            }
        }

        return { allAnswered, eligible, failedQuestion };
    };

    const proceedToConsent = () => {
        setCurrentStep('consent');
    };

    // Consent handling
    const toggleConsent = (itemId, isMandatory) => {
        if (isMandatory) {
            setConsents(prev => ({
                ...prev,
                mandatory: { ...prev.mandatory, [itemId]: !prev.mandatory[itemId] }
            }));
        } else {
            setConsents(prev => ({
                ...prev,
                optional: { ...prev.optional, [itemId]: !prev.optional[itemId] }
            }));
        }
    };

    const validateConsent = () => {
        const allMandatoryChecked = Object.values(consents.mandatory).every(v => v === true);
        const signatureValid = !studyData?.eConsent?.signatureRequired || hasSignature;
        return allMandatoryChecked && signatureValid;
    };

    // Signature canvas handlers
    const startDrawing = (e) => {
        isDrawingRef.current = true;
        const canvas = signatureCanvasRef.current;
        const ctx = signatureCtxRef.current;
        const rect = canvas.getBoundingClientRect();

        let x, y;
        if (e.type === 'mousedown') {
            x = e.clientX - rect.left;
            y = e.clientY - rect.top;
        } else if (e.type === 'touchstart') {
            x = e.touches[0].clientX - rect.left;
            y = e.touches[0].clientY - rect.top;
        }

        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const draw = (e) => {
        if (!isDrawingRef.current) return;
        const canvas = signatureCanvasRef.current;
        const ctx = signatureCtxRef.current;
        const rect = canvas.getBoundingClientRect();

        let x, y;
        if (e.type === 'mousemove') {
            x = e.clientX - rect.left;
            y = e.clientY - rect.top;
        } else if (e.type === 'touchmove') {
            e.preventDefault();
            x = e.touches[0].clientX - rect.left;
            y = e.touches[0].clientY - rect.top;
        }

        ctx.lineTo(x, y);
        ctx.stroke();
        setHasSignature(true);
    };

    const stopDrawing = () => {
        isDrawingRef.current = false;
    };

    const clearSignature = () => {
        const canvas = signatureCanvasRef.current;
        const ctx = signatureCtxRef.current;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    };

    const proceedToDetails = () => {
        // Capture signature data before leaving consent step (canvas won't be available after)
        if (studyData.eConsent?.signatureRequired && signatureCanvasRef.current && hasSignature) {
            setSignatureDataUrl(signatureCanvasRef.current.toDataURL('image/png'));
        }
        setCurrentStep('details');
    };

    // Details validation
    const validateDetails = () => {
        const emailValid = participantEmail && participantEmail === participantEmailConfirm;
        const nameValid = participantName.trim().length > 0;
        return emailValid && nameValid;
    };

    // Generate a unique access code (XXX-XXX format)
    const generateAccessCode = () => {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        let part1 = '';
        let part2 = '';
        for (let i = 0; i < 3; i++) {
            part1 += chars.charAt(Math.floor(Math.random() * chars.length));
            part2 += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return `${part1}-${part2}`;
    };

    // Get or create access code for this study
    const getOrCreateAccessCode = async () => {
        const studySlug = studyData.studyId;

        // Check if a predefined code exists in Codes table
        try {
            const checkResponse = await fetch(`/api/get-code?type=study-access&key=${encodeURIComponent(studySlug)}`);
            const checkResult = await checkResponse.json();

            if (checkResult.found && checkResult.valid && checkResult.code) {
                // Predefined code exists and is valid
                return { code: checkResult.code, source: 'predefined' };
            }

            if (checkResult.found && !checkResult.valid) {
                // Code exists but is invalid
                throw new Error(checkResult.reason || 'Access code is not available');
            }
        } catch (err) {
            // If it's a validation error, rethrow it
            if (err.message && err.message.includes('not available')) {
                throw err;
            }
            // Otherwise, continue to generate a new code
            console.log('No predefined code found, generating new one');
        }

        // No predefined code - generate one on frontend
        const newCode = generateAccessCode();
        return { code: newCode, source: 'frontend' };
    };

    const completeEnrollment = async () => {
        setIsProcessing(true);

        try {
            // Use captured signature data (captured when proceeding from consent step)
            const signatureData = signatureDataUrl;

            // Get or create access code
            const { code: finalCode, source: codeSource } = await getOrCreateAccessCode();

            // Call the unified transaction API
            const response = await fetch('/api/complete-transaction', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    type: 'study-enrollment',
                    slug: studyData.studyId,
                    customerName: participantName,
                    customerEmail: participantEmail,
                    screeningAnswers: eligibilityAnswers,
                    consentItems: {
                        mandatory: Object.keys(consents.mandatory).filter(k => consents.mandatory[k]),
                        optional: Object.keys(consents.optional).filter(k => consents.optional[k])
                    },
                    signatureData,
                    consentedAt: new Date().toISOString(),
                    accessCode: finalCode,
                    accessCodeSource: codeSource
                })
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.error || 'Enrolment failed');
            }

            setAccessCode(finalCode);
            setCurrentStep('success');
        } catch (err) {
            console.error('Enrollment error:', err);
            alert(err.message || 'There was an error completing your enrolment. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const handleClose = () => {
        document.body.style.overflow = '';
        onClose();
    };

    if (!isOpen || !studyData) return null;

    const eligibilityStatus = checkEligibility();
    const progressWidth = currentStep === 'eligibility' ? '25%' :
                          currentStep === 'consent' ? '50%' :
                          currentStep === 'details' ? '75%' : '100%';

    return (
        <div className="enrollment-modal-overlay" onClick={handleClose}>
            <div className="enrollment-modal" onClick={e => e.stopPropagation()}>
                <div className="progress-bar"><div className="progress-fill" style={{width: progressWidth}}></div></div>

                {/* Step 1: Eligibility */}
                {currentStep === 'eligibility' && (
                    <>
                        <div className="modal-header">
                            <div>
                                <div className="modal-title">Eligibility Check</div>
                                <div className="modal-step">Step 1 of 4</div>
                            </div>
                            <button className="modal-close" onClick={handleClose}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p style={{color: '#666', marginBottom: '1.5rem'}}>
                                Please answer the following questions to check if you're eligible to participate in the {studyData.studyAcronym} study.
                            </p>

                            {studyData.screeningSurvey?.questions?.map((question, idx) => (
                                <div key={idx} className="screening-question">
                                    <div className="question-text">{question.question}</div>
                                    <div className="question-options">
                                        {question.type === 'yesno' ? (
                                            <>
                                                <button
                                                    className={`option-btn ${eligibilityAnswers[question.id] === true ? 'selected selected-yes' : ''}`}
                                                    onClick={() => selectAnswer(question.id, true)}
                                                >
                                                    Yes
                                                </button>
                                                <button
                                                    className={`option-btn ${eligibilityAnswers[question.id] === false ? 'selected selected-no' : ''}`}
                                                    onClick={() => selectAnswer(question.id, false)}
                                                >
                                                    No
                                                </button>
                                            </>
                                        ) : question.options?.map((opt, optIdx) => (
                                            <button
                                                key={optIdx}
                                                className={`option-btn ${eligibilityAnswers[question.id] === opt ? 'selected' : ''}`}
                                                onClick={() => selectAnswer(question.id, opt)}
                                            >
                                                {opt}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}

                            {eligibilityStatus.allAnswered && !eligibilityStatus.eligible && (
                                <div className="ineligible-message">
                                    <h3>Unfortunately, you're not eligible</h3>
                                    <p>{eligibilityStatus.failedQuestion?.disqualifyMessage || 'Based on your answers, you don\'t meet the eligibility criteria for this study.'}</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary btn-sm" onClick={handleClose}>Cancel</button>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={proceedToConsent}
                                disabled={!eligibilityStatus.allAnswered || !eligibilityStatus.eligible}
                            >
                                Continue
                            </button>
                        </div>
                    </>
                )}

                {/* Step 2: Consent */}
                {currentStep === 'consent' && (
                    <>
                        <div className="modal-header">
                            <div>
                                <div className="modal-title">Informed Consent</div>
                                <div className="modal-step">Step 2 of 4</div>
                            </div>
                            <button className="modal-close" onClick={handleClose}>&times;</button>
                        </div>
                        <div className="modal-body">
                            {/* Participant Information Sheet */}
                            <div className="pis-container">
                                <h4>Participant Information Sheet</h4>
                                {studyData.eConsent?.participantInfoSheet?.content ? (
                                    <div dangerouslySetInnerHTML={{__html: studyData.eConsent.participantInfoSheet.content}} />
                                ) : (
                                    <>
                                        <p><strong>Study Title:</strong> {studyData.studyName}</p>
                                        {studyData.principalInvestigator && (
                                            <p><strong>Chief Investigator:</strong> {studyData.principalInvestigator.name}, {studyData.principalInvestigator.institution}</p>
                                        )}
                                        <h4>What is the purpose of this study?</h4>
                                        <p>{studyData.shortDescription || studyData.headline}</p>
                                    </>
                                )}
                            </div>

                            {/* Mandatory Consent Items */}
                            {studyData.eConsent?.mandatoryItems?.length > 0 && (
                                <div className="consent-items">
                                    <div className="consent-section-title">Mandatory Consent Items</div>
                                    {studyData.eConsent.mandatoryItems.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className={`consent-item ${consents.mandatory[item.id] ? 'checked' : ''}`}
                                            onClick={() => toggleConsent(item.id, true)}
                                        >
                                            <div className="consent-checkbox">
                                                {consents.mandatory[item.id] && '✓'}
                                            </div>
                                            <div className="consent-text">
                                                {item.text}<span className="consent-required">*</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Optional Consent Items */}
                            {studyData.eConsent?.optionalItems?.length > 0 && (
                                <div className="consent-items">
                                    <div className="consent-section-title">Optional Consent Items</div>
                                    {studyData.eConsent.optionalItems.map((item, idx) => (
                                        <div
                                            key={idx}
                                            className={`consent-item ${consents.optional[item.id] ? 'checked' : ''}`}
                                            onClick={() => toggleConsent(item.id, false)}
                                        >
                                            <div className="consent-checkbox">
                                                {consents.optional[item.id] && '✓'}
                                            </div>
                                            <div className="consent-text">{item.text}</div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Signature */}
                            {studyData.eConsent?.signatureRequired && (
                                <div className="signature-section">
                                    <label>Your Signature</label>
                                    <div className="signature-canvas-container">
                                        <canvas
                                            ref={signatureCanvasRef}
                                            className="signature-canvas"
                                            onMouseDown={startDrawing}
                                            onMouseMove={draw}
                                            onMouseUp={stopDrawing}
                                            onMouseLeave={stopDrawing}
                                            onTouchStart={startDrawing}
                                            onTouchMove={draw}
                                            onTouchEnd={stopDrawing}
                                        />
                                        <button className="signature-clear" onClick={clearSignature}>Clear</button>
                                    </div>
                                    <p className="signature-note">Sign using your mouse or finger</p>
                                </div>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary btn-sm" onClick={() => setCurrentStep('eligibility')}>Back</button>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={proceedToDetails}
                                disabled={!validateConsent()}
                            >
                                Continue
                            </button>
                        </div>
                    </>
                )}

                {/* Step 3: Details */}
                {currentStep === 'details' && (
                    <>
                        <div className="modal-header">
                            <div>
                                <div className="modal-title">Your Details</div>
                                <div className="modal-step">Step 3 of 4</div>
                            </div>
                            <button className="modal-close" onClick={handleClose}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <p style={{color: '#666', marginBottom: '1.5rem'}}>
                                Please provide your details so we can set up your study account.
                            </p>

                            <div className="form-group">
                                <label className="form-label">Full Name</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={participantName}
                                    onChange={(e) => setParticipantName(e.target.value)}
                                    placeholder="Enter your full name"
                                    autoComplete="name"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Email Address</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={participantEmail}
                                    onChange={(e) => setParticipantEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    autoComplete="email"
                                />
                            </div>

                            <div className="form-group">
                                <label className="form-label">Confirm Email Address</label>
                                <input
                                    type="email"
                                    className="form-input"
                                    value={participantEmailConfirm}
                                    onChange={(e) => setParticipantEmailConfirm(e.target.value)}
                                    placeholder="Confirm your email"
                                    autoComplete="email"
                                />
                                {participantEmailConfirm && participantEmail !== participantEmailConfirm && (
                                    <p style={{color: '#dc3545', fontSize: '0.85rem', marginTop: '0.25rem'}}>
                                        Email addresses do not match
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn btn-secondary btn-sm" onClick={() => setCurrentStep('consent')}>Back</button>
                            <button
                                className="btn btn-primary btn-sm"
                                onClick={completeEnrollment}
                                disabled={!validateDetails() || isProcessing}
                            >
                                {isProcessing ? 'Processing...' : 'Complete Enrolment'}
                            </button>
                        </div>
                    </>
                )}

                {/* Step 4: Success */}
                {currentStep === 'success' && (
                    <>
                        <div className="modal-header">
                            <div>
                                <div className="modal-title">Enrolment Complete!</div>
                                <div className="modal-step">Step 4 of 4</div>
                            </div>
                            <button className="modal-close" onClick={handleClose}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="success-screen">
                                <div className="success-icon">🎉</div>
                                <h2 className="success-title">Welcome to the {studyData.studyAcronym} Study!</h2>
                                <p className="success-message">
                                    Thank you for enrolling. Your participation will help advance important research.
                                </p>

                                <AccessCodeDisplay
                                    accessCode={accessCode}
                                    appId={studyData?.mobileApp?.appId || DEFAULT_APP}
                                    variant="gradient"
                                />

                                <div className="next-steps">
                                    <h4>Or follow these steps:</h4>
                                    <ol>
                                        <li>Download the {getAppConfig(studyData?.mobileApp?.appId || DEFAULT_APP).name} app from the App Store or Google Play</li>
                                        <li>Open the app and tap "Join a Study"</li>
                                        <li>Enter your access code: <strong>{accessCode}</strong></li>
                                        <li>Complete the initial setup questionnaire</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer" style={{justifyContent: 'center'}}>
                            <button className="btn btn-primary btn-sm" onClick={handleClose}>Done</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default EnrollmentModal;
